import React, { useRef, useState } from "react";
import { updateTenantLogo } from "../../services/authService";
import { canMutateTenantData } from "../../config/constants";

const MAX_LOGO_SIZE_BYTES = 10 * 1024 * 1024;

const TenantLogoControl = ({
  tenant,
  userRole,
  onLogoUpdated,
  isSuperAdmin = false,
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  if (isSuperAdmin || !canMutateTenantData(userRole)) {
    return null;
  }

  const hasLogo =
    tenant?.logo &&
    typeof tenant.logo === "string" &&
    tenant.logo.startsWith("data:image/");

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setError("");
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (PNG, JPEG, GIF, or WebP).");
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setError("Logo must be 10 MB or smaller.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) {
        setError("Failed to read the image.");
        return;
      }
      setUploading(true);
      try {
        const result = await updateTenantLogo(dataUrl);
        if (result.success) {
          if (onLogoUpdated && result.user) {
            onLogoUpdated(result.user);
          }
        } else {
          setError(result.message || "Failed to update logo.");
        }
      } catch {
        setError("Failed to update logo.");
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => setError("Failed to read the image.");
    reader.readAsDataURL(file);
  };

  const openPicker = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) fileInputRef.current?.click();
  };

  return (
    <div className="tenant-logo-control">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        className="hidden"
        onChange={handleFileChange}
        aria-hidden
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={uploading}
        className="tenant-logo-edit"
        title={uploading ? "Saving…" : hasLogo ? "Change company logo" : "Add company logo"}
        aria-label={hasLogo ? "Change company logo" : "Add company logo"}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      </button>
      {error ? (
        <span className="tenant-logo-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default TenantLogoControl;
